import * as yup from "yup";
import {
  HandleChange as FieldChange,
  HandleDateChange as DateFieldChange,
  OnChange,
  InvestorFundDetails,
  Options,
  SwitchInOptions,
} from "../../../../../store/types";
import {
  getStpFolios,
  getStpFundOptions,
  getStpSchemes,
  getSwitchOutOptions,
} from "..";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { useAppSelector } from "../../../../../store/hooks";
import {
  getSelectedCartItem,
  getUpdatedValues,
  handleError,
} from "../../../../../utils/cart";

import {
  addItemsToCart,
  getAssetClasses,
  getMinTatStp,
  getMinimumAmount,
  getStpDayOptions,
  getStpFrequency,
  getStpSchemesList,
  getWithdrawalOptions,
  updateCartItem,
  validateSipDates,
} from "../../../../../store/actions";
import { getFrequencyName } from "../../../transactions/tranUtils";
import {
  calculateNoOfInstallments,
  getWeekendCount,
  capitalize,
  getMinimumEndDate,
  getMinimumStartDate,
  handleCalendarView,
  shouldDisableEndDate,
  shouldDisableStartDate,
} from "../../../../../utils/systematic-transactions";

import { Frequency } from "../../../../../store/types/transactions";
import moment from "moment";

export interface LocationState {
  cartId: string;
  cartItemId: string | number;
  amc: string;
}

interface HandleChange extends FieldChange<Values> {}
interface HandleDateChange extends DateFieldChange<Values> {}
export interface Values {
  amc: string;
  folio: string | number;
  switchOutScheme: string;
  stpFrequency: string;
  minimumInstallments: number;
  minAmount: number;
  maxAmount: number;
  category: string;
  switchInScheme: string;
  noOfTransfers: number;
  stpDate: string;
  stpStartDate: Date;
  stpEndDate: Date | undefined;
  stpOption: string;
  amount: string;
}

const FIELD_NAMES: Array<keyof Values> = [
  "amc",
  "folio",
  "switchOutScheme",
  "category",
  "switchInScheme",
  "stpFrequency",
  "minimumInstallments",
  "minAmount",
  "maxAmount",
  "noOfTransfers",
  "stpDate",
  "stpStartDate",
  "stpEndDate",
  "stpOption",
  "amount",
];

const resetLaterFields = getUpdatedValues<Values>(FIELD_NAMES);

export default function useStp() {
  const dispatch = useDispatch();

  const location = useLocation<LocationState>();
  const history = useHistory();

  const { fundDetails } = useAppSelector((state) => state.cartReducer);
  // @ts-ignore
  const cartItems: CartItems<"STP"> = useAppSelector(
    (state) => state.cartReducer
  ).cartItems;

  const [initialState, setInitialState] = useState<Values>({
    amc: "",
    folio: "",
    switchOutScheme: "",
    category: "",
    switchInScheme: "",
    stpFrequency: "",
    minimumInstallments: 0,
    minAmount: 0,
    maxAmount: 0,
    amount: "",
    stpDate: "",
    stpEndDate: undefined,
    stpOption: "",
    noOfTransfers: 0,
    stpStartDate: undefined,
  });

  const [startDateMin, setStartDateMin] = useState<Date>(new Date());
  const [endDateMin, setEndDateMin] = useState<Date>(new Date());

  const [categoryOptions, setCategoryOptions] = useState<Options>([]);
  const [switchInOptions, setSwitchInOptions] = useState<SwitchInOptions>([]);
  const [frequencyOptions, setFrequencyOptions] = useState<
    { label: string; value: string; minimumInstallments: number }[]
  >([]);
  const [stpDayOptions, setStpDayOptions] = useState<Options>([]);
  const [stpOptions, setStpOptions] = useState<Options>([]);
  const [switchOutOptions, setSwitchOutOptions] = useState<
    ReturnType<typeof getStpSchemes>
  >([]);
  const [folioOptions, setFolioOptions] = useState<Options>([]);

  const { state, pathname } = location;
  const isEdit = pathname.includes("/edit-transaction");

  // @ts-ignore
  const { payload } = getSelectedCartItem<"STP">(cartItems, state);

  const fundOptions = useMemo(
    () => getStpFundOptions(fundDetails),
    [fundDetails]
  );

  const setInitialValues = async () => {
    const { folio } = payload;
    const switchOptions = getStpSchemes(fundDetails, state.amc, folio);
    const updatedFolioOptions = getStpFolios(fundDetails, state.amc);
    setFolioOptions(updatedFolioOptions);

    setSwitchOutOptions(switchOptions);
    const selectedSwitchOutScheme = switchOptions.find(
      ({ scheme, plan, option, details }) =>
        [
          option === payload.option,
          scheme === payload.scheme,
          plan === payload.plan,
          details.Acno.toString() === folio.toString(),
        ].every(Boolean)
    );
    const assetClasses = await getAssetClasses({
      Fund: state.amc,
      Folio: folio,
    })(dispatch);
    const { DtData = [] } = assetClasses;

    setCategoryOptions(
      DtData.map((cat: { CatDesc: string; CatValue: string }) => ({
        label: cat.CatDesc,
        value: cat.CatValue,
      }))
    );
    const requestBody = {
      folio: payload.folio,
      fundCode: state.amc,
      category: payload.category,
    };
    const response = await getStpSchemesList(requestBody)(dispatch);
    const newSwitchInOptions = response.filter(
      ({ fm_scheme, fm_plan, fm_option }) => {
        const { scheme, plan, option } = selectedSwitchOutScheme;
        return !(
          fm_scheme === scheme &&
          plan === fm_plan &&
          option === fm_option
        );
      }
    );

    setSwitchInOptions(newSwitchInOptions);
    const selectedSwitchInScheme = newSwitchInOptions.find(
      ({ fm_option, fm_plan, fm_scheme }) =>
        [
          fm_option === payload.toOption,
          fm_plan === payload.toPlan,
          fm_scheme === payload.toScheme,
        ].every(Boolean)
    );

    const newFrequencyOptions = await getStpFrequency(state.amc)(dispatch);
    setFrequencyOptions(newFrequencyOptions);

    const stpDayOptions = await getStpDayOptions(
      state.amc,
      getFrequencyName(payload.frequency)
    )(dispatch);

    setStpDayOptions(stpDayOptions);
    const transferOptions = await getWithdrawalOptions(state.amc)(dispatch);

    setStpOptions(transferOptions);

    const selectedFrequencyOption = newFrequencyOptions.find(
      ({ value }) => value === getFrequencyName(payload.frequency)
    );

    const minAmountResponse = await getMinimumAmount({
      Fund: state.amc,
      Trtype: "STP",
      Frequency: getFrequencyName(payload.frequency),
      Scheme: selectedSwitchInScheme.fm_scheme,
    })(dispatch);
    const { minamt } = minAmountResponse.DtData[0];

    setInitialState((values) => ({
      ...values,
      minimumInstallments: Number(selectedFrequencyOption.minimumInstallments),
      minAmount: minamt,
      maxAmount: Number(selectedSwitchOutScheme.details.CurValue),
      stpFrequency: selectedFrequencyOption.value,
      amc: state.amc,
      amount: payload.stpOption === "Fixed" ? payload.amount : "",
      category: payload.category,
      folio,
      noOfTransfers: Number(payload.noOfTransfers),
      stpDate: payload.stpDay.toString(),
      stpEndDate: new Date(payload.stpEndDate),
      stpStartDate: new Date(payload.stpStartDate),
      stpOption: payload.stpOption === "Fixed" ? "F" : "C",
      switchOutScheme: selectedSwitchOutScheme.value,
      switchInScheme: selectedSwitchInScheme?.value || "",
    }));
  };

  useEffect(() => {
    if (!isEdit) return;
    (async function () {
      try {
        await setInitialValues();
      } catch (error) {
        handleError(error);
      }
    })();
  }, [pathname]);

  useEffect(() => {
    if (!isEdit) return;
    if (!initialState?.switchInScheme) return;
    setStpDates(initialState);
  }, [initialState]);

  const handleCategoryChange: HandleChange = function (values, setValues) {
    return async (e) => {
      const category = e.target.value;
      const updatedValues = resetLaterFields(values, "category", {
        category,
      });
      setValues(updatedValues);

      const selectedSwitchOutScheme = switchOutOptions.find(
        ({ value, details }) =>
          value === values.switchOutScheme &&
          details.Acno.toString() === values.folio.toString()
      );

      const requestBody = {
        folio: values.folio,
        fundCode: values.amc,
        category: e.target.value,
      };
      const response = await getStpSchemesList(requestBody)(dispatch);

      const updatedSwitchInOptions = response.filter(
        ({ fm_scheme, fm_plan, fm_option }) => {
          const { scheme, plan, option } = selectedSwitchOutScheme;
          return ![
            fm_scheme === scheme,
            plan === fm_plan,
            option === fm_option,
          ].every(Boolean);
        }
      );
      setSwitchInOptions(updatedSwitchInOptions);
    };
  };

  const handleSwitchInSchemeChange: HandleChange = function (
    values,
    setValues
  ) {
    return async (e) => {
      const switchInScheme = e.target.value;
      const updatedValues = resetLaterFields(values, "switchInScheme", {
        switchInScheme,
      });
      setValues(updatedValues);
      const response = await getStpFrequency(values.amc)(dispatch);
      setFrequencyOptions(response);
    };
  };

  const handleSwitchOutSchemeChange: HandleChange = function (
    values,
    setValues
  ) {
    return async (e) => {
      try {
        const switchOutScheme = e.target.value;
        const requestBody = { Fund: values.amc, Folio: values.folio };
        const assetClasses = await getAssetClasses(requestBody)(dispatch);

        const { DtData = [] } = assetClasses;

        const updatedCategories = DtData.map(
          (cat: { CatDesc: string; CatValue: string }) => ({
            label: cat.CatDesc,
            value: cat.CatValue,
          })
        );
        setCategoryOptions(updatedCategories);

        const updatedValues = resetLaterFields(values, "switchOutScheme", {
          switchOutScheme,
        });
        setValues(updatedValues);
      } catch (error) {
        alert(error);
      }
    };
  };
  const handleFrequencyChange: HandleChange = function (values, setValues) {
    return async (e: OnChange) => {
      try {
        const stpFrequency = e.target.value;

        setStpDayOptions(
          await getStpDayOptions(values.amc, e.target.value)(dispatch)
        );
        setStpOptions(await getWithdrawalOptions(values.amc)(dispatch));
        const selectedSwitchOutScheme = switchOutOptions.find(
          ({ value }) => value === values.switchOutScheme
        )!;
        const selectedSwitchInScheme = switchInOptions.find(
          (item) => values.switchInScheme === item.value
        );
        const payload = {
          Fund: values.amc,
          Trtype: "STP",
          Frequency: e.target.value,
          Scheme: selectedSwitchInScheme.fm_scheme,
        };

        const response = await getMinimumAmount(payload)(dispatch);
        const { DtData } = response;
        const updatedValues = resetLaterFields(values, "stpFrequency", {
          stpFrequency,
          minimumInstallments: DtData[0].mininst,
          minAmount: DtData[0].minamt,
          maxAmount: Number(selectedSwitchOutScheme.details.CurValue),
        });
        await setStpDates(updatedValues);
        setValues(updatedValues);
      } catch (e) {
        handleError(e);
        setValues({ ...values, stpFrequency: "" });
      }
    };
  };

  const handleInstallmentsChange: HandleChange = function (values, setValues) {
    return (e) => {
      const noOfTransfers = e.target.value;
      const updatedValues = resetLaterFields(values, "noOfTransfers", {
        noOfTransfers,
      });
      setValues(updatedValues);
    };
  };

  const setStpDates = async function (values: Values): Promise<void> {
    try {
      const selectedSwitchInScheme = switchInOptions.find(
        (item) => values.switchInScheme === item.value
      );
      const response = await getMinTatStp({
        frequency: values.stpFrequency,
        scheme: selectedSwitchInScheme.fm_scheme,
        fund: values.amc,
      })(dispatch);

      const stpDate = moment().add(response, "days").date();
      const validSipStartDate = getMinimumStartDate({
        sipTat: response,
        sipDay: isNaN(Number(stpDate)) ? stpDate : Number(stpDate),
        frequency: values.stpFrequency as Frequency,
      });
      const endDateMin = getMinimumEndDate({
        startDate: validSipStartDate,
        frequency: capitalize(values.stpFrequency) as Frequency,
        minimumInstallments: Number(values.minimumInstallments),
      });
      setStartDateMin(validSipStartDate);
      setEndDateMin(endDateMin);
    } catch (error) {
      handleError(error);
    }
  };

  const handleStartDateChange: HandleDateChange = function (values, helpers) {
    return async function (startDate, value) {
      const { setValues } = helpers;
      try {
        const endDateMin = getMinimumEndDate({
          frequency: capitalize(values.stpFrequency) as Frequency,
          minimumInstallments: Number(values.minimumInstallments),
          startDate: startDate,
        });

        const noOfInstallments = calculateNoOfInstallments({
          frequency: capitalize(values.stpFrequency) as Frequency,
          endDate: endDateMin,
          startDate: startDate,
        });
        const updatedDateForm = resetLaterFields(values, "stpStartDate", {
          stpStartDate: startDate,
          noOfTransfers: noOfInstallments,
          stpDate: moment(startDate).date().toString(),
        });
        setValues(updatedDateForm);
        setEndDateMin(endDateMin);
      } catch (error) {
        if (error instanceof Error)
          helpers.setFieldError("stpStartDate", error.message);
      }
    };
  };

  function getInstallments(values: Values) {
    if (values.stpFrequency === "DAILY") {
      const noOfInstallments =
        calculateNoOfInstallments({
          frequency: capitalize(values.stpFrequency) as Frequency,
          endDate: values.stpEndDate,
          startDate: values.stpStartDate,
        }) - getWeekendCount(values.stpStartDate, values.stpEndDate);

      return noOfInstallments;
    }
    return calculateNoOfInstallments({
      frequency: capitalize(values.stpFrequency) as Frequency,
      endDate: values.stpEndDate,
      startDate: values.stpStartDate,
    });
  }

  const handleEndDateChange: HandleDateChange = function (values, helpers) {
    return async function (endDate) {
      try {
        const noOfInstallments = getInstallments({
          ...values,
          stpEndDate: endDate,
        });

        const updatedValues = resetLaterFields(values, "stpEndDate", {
          stpEndDate: endDate,
          noOfTransfers: noOfInstallments,
        });

        const selectedSwitchInScheme = switchInOptions.find(
          ({ value }) => value === values.switchInScheme
        );

        const { fm_scheme } = selectedSwitchInScheme;

        await validateSipDates({
          endDate: endDate,
          frequency: values.stpFrequency,
          fund: values.amc,
          scheme: fm_scheme,
          startDate: values.stpStartDate,
          trType: "STP",
        })(dispatch);

        helpers.setValues(updatedValues);
      } catch (error) {
        handleError(error);
        if (error instanceof Error) {
          helpers.setFieldError("stpStartDate", error.message);
          helpers.setFieldError("stpEndDate", error.message);
          helpers.setFieldValue("stpStartDate", "");
          helpers.setFieldValue("stpEndDate", "");
        }
      }
    };
  };

  const handleAmcChange: HandleChange = function (values, setValues) {
    return (e) => {
      const amc = e.target.value;
      const updatedValues = resetLaterFields(values, "amc", { amc });
      const updatedFolioOptions = getStpFolios(fundDetails, amc);
      setValues(updatedValues);
      setFolioOptions(updatedFolioOptions);
    };
  };

  const handleFolioChange: HandleChange = function (values, setValues) {
    return (e) => {
      const folio = e.target.value;
      if (!folio) return;
      const switchOptions = getStpSchemes(fundDetails, values.amc, folio);
      setSwitchOutOptions(switchOptions);
      const updatedValues = resetLaterFields(values, "folio", { folio });
      setValues(updatedValues);
    };
  };

  const onSubmit = async (values: Values) => {
    const selectedCartItem = cartItems.find(
      ({ cartItemId: id }: any) => id === state.cartItemId
    );

    const { payload } = selectedCartItem;

    const selectedFolioDetails = fundDetails.find(({ Acno, Fund, PAN }) => {
      if (values.amc !== Fund) return false;
      if (values.folio.toString() !== Acno.toString()) return false;
      if (payload.pan !== PAN) return false;
      return true;
    });

    const { Mobile: mobile = "", email = "" } =
      selectedFolioDetails as InvestorFundDetails;

    const {
      amc,
      amount,
      folio,
      noOfTransfers,
      stpDate,
      stpFrequency,
      stpOption,
      switchInScheme,
      switchOutScheme,
    } = values;

    const switchOutOptions = getSwitchOutOptions(fundDetails, amc, folio);

    const selectedSwitchInScheme = switchInOptions.find(
      ({ value }) => value === switchInScheme
    ) as SwitchInOptions[number];

    const selectedSwitchOutScheme = switchOutOptions.find(
      ({ value }: any) => value === switchOutScheme
    );

    const requestBody = {
      ...payload,
      mobile,
      email,
      cartId: state.cartId,
      cartItemId: state.cartItemId,
      fund: amc,
      folio,
      scheme: selectedSwitchOutScheme.scheme,
      plan: selectedSwitchOutScheme.plan,
      option: selectedSwitchOutScheme.option,
      toScheme: selectedSwitchInScheme.fm_scheme,
      toPlan: selectedSwitchInScheme.fm_plan,
      toOption: selectedSwitchInScheme.fm_option,
      amount: stpOption === "F" ? amount : 0,
      stpOption: stpOption === "F" ? "Fixed" : "Capital Appreciation",
      withdrawalType: stpOption === "F" ? "Fixed" : "Capital Appreciation",
      switchInSchemeName: selectedSwitchInScheme.label,
      switchOutSchemeName: selectedSwitchOutScheme.label,
      noOfTransfers,
      frequency: getFrequencyName(stpFrequency),
      stpDay: stpDate,
      stpStartDate: moment(values.stpStartDate).format("MM/DD/YYYY"),
      stpEndDate: moment(values.stpEndDate).format("MM/DD/YYYY"),
      category: values.category,
      taxSaverFlag: selectedSwitchOutScheme.details.TaxSaverFlag,
    };

    const response = await updateCartItem(requestBody)(dispatch);

    if (!!response) {
      alert("Item has been updated successfully");
      history.push("/cart/investor-cart", {
        pan: payload.pan,
      });
    }
  };

  const onAddTransaction = async (values: Values) => {
    const {
      amc,
      amount,
      folio,
      noOfTransfers,
      stpDate,
      stpFrequency,
      stpOption,
      switchInScheme,
      switchOutScheme,
    } = values;

    const { Mobile: mobile, email } = fundDetails.find(
      ({ Acno, Fund, PAN }) => {
        if (values.amc !== Fund) return false;
        if (values.folio.toString() !== Acno.toString()) return false;
        if (payload.pan !== PAN) return false;
        return true;
      }
    );
    const switchOutOptions = getSwitchOutOptions(fundDetails, amc, folio);

    const selectedSwitchInScheme = switchInOptions.find(
      ({ value }) => value === switchInScheme
    );
    const selectedSwitchOutScheme = switchOutOptions.find(
      ({ value }: any) => value === switchOutScheme
    );

    const selectedFund = fundOptions.find((i) => i.value === amc).label;

    const requestBody = {
      ...payload,
      mobile,
      email,
      fund: amc,
      folio,
      scheme: selectedSwitchOutScheme.scheme,
      plan: selectedSwitchOutScheme.plan,
      option: selectedSwitchOutScheme.option,
      toScheme: selectedSwitchInScheme.fm_scheme,
      toPlan: selectedSwitchInScheme.fm_plan,
      toOption: selectedSwitchInScheme.fm_option,
      amount: stpOption === "F" ? amount : 0,
      stpOption: stpOption === "F" ? "Fixed" : "Capital Appreciation",
      withdrawalType: stpOption === "F" ? "Fixed" : "Capital Appreciation",
      switchInSchemeName: selectedSwitchInScheme.label,
      switchOutSchemeName: selectedSwitchOutScheme.label,
      noOfTransfers,
      frequency: getFrequencyName(stpFrequency),
      stpDay: stpDate,
      stpStartDate: moment(values.stpStartDate).format("MM/DD/YYYY"),
      stpEndDate: moment(values.stpEndDate).format("MM/DD/YYYY"),
      category: values.category,
      taxSaverFlag: selectedSwitchOutScheme.details.TaxSaverFlag,
      fundName: selectedFund,
    };

    const response = await dispatch(addItemsToCart(requestBody));

    if (!!response) {
      history.replace("/cart/investor-cart", {
        pan: payload.pan,
      });
    }
  };

  function goToCart() {
    history.replace("/cart/investor-cart", {
      pan: payload.pan,
    });
  }

  return {
    changeHandlers: {
      amc: handleAmcChange,
      category: handleCategoryChange,
      folio: handleFolioChange,
      installment: handleInstallmentsChange,
      frequency: handleFrequencyChange,
      switchIn: handleSwitchInSchemeChange,
      switchOut: handleSwitchOutSchemeChange,
      handleEndDateChange,
      handleStartDateChange,
    },
    options: {
      categoryOptions,
      switchInOptions,
      frequencyOptions,
      stpDayOptions,
      stpOptions,
      switchOutOptions,
      fundOptions,
      folioOptions,
    },

    dateProps: {
      handleCalendarView,
      shouldDisableStartDate,
      shouldDisableEndDate,
      startDate: {
        minDate: startDateMin,
      },
      endDate: {
        minDate: endDateMin,
      },
    },
    onSubmit,
    onAddTransaction,
    goToCart,
    isEdit,
    validationSchema: validationSchema(fundDetails),
    investorPreview: [
      { label: "Investor Name", value: payload.investorName },
      { label: "PAN", value: payload.pan },
      { label: "Email", value: payload.email },
      { label: "Mobile", value: payload.mobile },
      {
        label: "EUIN Declaration",
        value: payload.euinDeclaration === "Y" ? "No" : "Yes",
      },
      { label: "EUIN", value: payload.euin || "-" },
      { label: "Sub ARN Code", value: payload.subArn || "-" },
      { label: "Sub Broker Code", value: payload.subBroker || "-" },
    ],
    initialState,
  };
}

const validationSchema = (fundDetails: InvestorFundDetails[]) =>
  yup.object().shape({
    amc: yup.string().required("AMC is required."),
    folio: yup
      .string()
      .required("Folio is required.")
      .test("jhf", "Joint holder folio not allowed", function (value) {
        if (!value) return true;
        const { amc } = this.parent;
        const isJointHolderFolio = fundDetails.some(
          (fund) =>
            fund.Fund === amc &&
            String(fund.Acno) === String(value) &&
            fund.notallowed_flag === "J"
        );
        return !isJointHolderFolio; 
      })
      .test("zbf", "Zero balance folios are not allowed", function (value) {
        if (!value) return true;
        const { amc } = this.parent;
        const switchOptions = getSwitchOutOptions(fundDetails, amc, value);
        return Boolean(switchOptions.length);
      }),
    switchOutScheme: yup.string().required("Transfer From Scheme is required."),
    category: yup.string().required("Category is required."),
    switchInScheme: yup.string().required("Transfer To Scheme is required."),
    stpFrequency: yup.string().required("STP Frequency is required."),
    stpDate: yup.string().required("STP Date is required."),
    stpStartDate: yup.string().required("Start Date is required."),
    stpEndDate: yup.string().required("End Date is required."),
    stpOption: yup.string().required("STP Option is required."),
    amount: yup.string().when("stpOption", {
      is: (value: string) =>
        !!value &&
        (value.toUpperCase() === "F" || value.toUpperCase() === "FIXED"),
      then: (schema) =>
        schema
          .required("Amount is required")
          .test("minimumAmount", "", function (value) {
            const { minAmount } = this.parent;
            if (!minAmount) return true;
            if (parseFloat(value) * (1 + 0.05) < parseFloat(minAmount)) {
              return this.createError({
                message: `Minimum amount (${minAmount}) is required`,
              });
            }
            return true;
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
